import { useEffect, useState } from "react";
import { Accordion, AccordionSummary, AccordionDetails, Typography, Box } from "@mui/material";
import { useTranslation } from "react-i18next";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SwitchComponent from "components/Checkbox";
import { usePushStore } from "store/pushStore";

const styles = {
  title: {
    marginBottom: "8px",
    fontFamily: "var(--cygreMedium), sans-serif",
    fontSize: "14px",
    fontWeight: "600",
    color: "#2E263D",
  },
  text: {
    paddingRight: "32px",
    fontSize: "14px",
    fontWeight: "400",
    lineHeight: 1.43,
    color: "#6D6777",
  },

  acordionDetails: {
    display: "flex",
    flexDirection: "column",
    gap: "24px",
    padding: { xs: "0px 16px 16px 16px", md: "0px 20px 20px 20px" },
  },
  container: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    width: "100%",
  },
  summary: {
    lineHeight: "32px",
    fontSize: "16px",
    color: "#2E263D",
    fontFamily: "var(--cygreMedium), sans-serif",
  },
  accordion: {
    boxShadow: "none",
    border: "1px solid #EEEEEE",
    borderRadius: "8px",
    "&::before": {
      content: "none",
    },
    "&.Mui-expanded": { margin: 0 },
  },
  acordionSummary: {
    padding: { xs: "0px 16px", md: "0px 20px" },
    height: { xs: "56px", md: "64px" },
  },
};

interface IOptional {
  options?: {
    notifyAgain?: boolean;
    awaitResponse?: boolean;
    silentMode?: boolean;
  };
}

export default function Optional({ options }: IOptional) {
  const { t } = useTranslation();
  const savePushData = usePushStore((state) => state.savePushData);

  const [notifyAgain, setNotifyAgain] = useState<boolean>(false);
  const [awaitResponse, setAwaitResponse] = useState<boolean>(false);
  const [silentMode, setSilentMode] = useState<boolean>(false);

  useEffect(() => {
    if (options) setNotifyAgain(options.notifyAgain || false);
    if (options) setAwaitResponse(options.awaitResponse || false);
    if (options) setSilentMode(options.silentMode || false);
  }, [options]);

  const handleSwitchChange = (key: "notifyAgain" | "awaitResponse" | "silentMode", checked: boolean) => {
    switch (key) {
      case "notifyAgain":
        setNotifyAgain(checked);
        break;
      case "awaitResponse":
        setAwaitResponse(checked);
        break;
      case "silentMode":
        setSilentMode(checked);
        break;
    }

    savePushData({ [key]: checked });
  };

  return (
    <Accordion sx={styles.accordion}>
      <AccordionSummary sx={styles.acordionSummary} expandIcon={<ExpandMoreIcon />}>
        <Typography sx={styles.summary}>{t("additional")}</Typography>
      </AccordionSummary>
      <AccordionDetails sx={styles.acordionDetails}>
        <Box sx={styles.container}>
          <Box sx={{ paddingRight: "32px" }}>
            <Typography sx={styles.title}>{t("notifyAgain")}</Typography>
            <Typography sx={styles.text}>{t("notifyAgainDescription")}</Typography>
          </Box>
          <SwitchComponent
            checked={notifyAgain}
            onChange={(e, checked) => handleSwitchChange("notifyAgain", checked)}
          />
        </Box>
        <Box sx={styles.container}>
          <Box sx={{ paddingRight: "32px" }}>
            <Typography sx={styles.title}>{t("awaitResponse")}</Typography>
            <Typography sx={styles.text}>{t("awaitResponseDescription")}</Typography>
          </Box>
          <SwitchComponent
            checked={awaitResponse}
            onChange={(e, checked) => handleSwitchChange("awaitResponse", checked)}
          />
        </Box>
        <Box sx={styles.container}>
          <Box sx={{ paddingRight: "32px" }}>
            <Typography sx={styles.title}>{t("silentMode")}</Typography>
            <Typography sx={styles.text}>{t("silentModeDescription")}</Typography>
          </Box>
          <SwitchComponent checked={silentMode} onChange={(e, checked) => handleSwitchChange("silentMode", checked)} />
        </Box>
      </AccordionDetails>
    </Accordion>
  );
}
